import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { HeroIllustration } from '../components/mte/hero-asset-img';
import { Layout, Header, Card, Button } from '../components/mte/shell';
import { HERO_CELLS, type HeroCell, type HeroId } from '../components/mte/placeholders';
import styles from './DashboardMonde.module.css';
import { getActiveProfile } from '../lib/profiles';

/**
 * DashboardMonde — écran 7/11 (D-NX-070, livré 2026-09-12).
 *
 * Centre de l'expérience enfant. Carte du monde magique où chaque chapitre
 * apparaît comme un nœud que la criança reconnaît visuellement.
 *
 * Source spec Boussole : `05-dashboard-map.md` (écrâ 11) + `02-user-flows.md` (F4).
 *
 * Route canon : `/enfant/aventure/` (cf. `mte-architecture/04-routing.md` §6.2).
 * Compatibilité ascendante : `/enfant/monde/` redirige vers `/enfant/aventure/`
 * (cf. App.tsx — petit shim de routing introduit dans ce commit).
 *
 * Marqueurs canon respectés :
 *  - 6 héros fixes (D-NX-038) — pas de réintroduction d'anciens archétypes
 *  - Sábio mentor UNIVERSEL, NON sélectionnable (D-NX-042)
 *  - Mundo único partilhé (D-NX-036 / M6) — pas de notion « mon monde vs autres »
 *  - Mission Rentrée = cartão-âncora destacado (D-MAP-01)
 *  - Diagnostic = capítulo paralelo, jamais bloqueant (D-MAP-02)
 *  - P1..P5 = chaîne séquentielle après Mission Rentrée (D-MAP-02)
 *  - Carte générée par interface, PAS image fixe (D-NX-036 / M6)
 *  - Sortie parent TOUJOURS visible dans AppBar (D-UF-03)
 *  - Pas de email/données parent exposées (D-NX-036 / B0)
 *  - Tap targets ≥ --touch-xl (96×96) sur cartes chapitres (D-PAL-CL-03)
 *
 * Mock Vague 2 (D-NX-066) :
 *  - Aucune persistence réelle (`PreviewAuthAdapter` câblera en Vague 3)
 *  - Profil hardcodé : Léo (CE1, héros ninja, étape 3/15)
 *  - Aucune lecture storage — l'état vit dans le composant
 *
 * Jalons 5/10/15 : marquage visuel sobre (texte mono + couleur accent),
 * pas de célébration cinématique en Vague 2 (D-NX-036 / M3b).
 *
 * États couverts :
 *  - `not_started` : Mission Rentrée non débutée (CTA « Commencer »)
 *  - `in_progress` : Mission Rentrée en cours (CTA « Continuer », jalons 5/10/15)
 *  - `completed`   : Mission Rentrée terminée (étoile, P1 débloqué)
 */

/* === Types ============================================================= */

type ChapterState = 'locked' | 'available' | 'in_progress' | 'done' | 'completed';

interface Chapter {
  id: ChapterId;
  title: string;
  icon: string;
  state: ChapterState;
  lockedNote?: string;
}

type MissionState = 'not_started' | 'in_progress' | 'completed';

type ChapterId =
  | 'mission-rentree'
  | 'diagnostic'
  | 'P1-nombres'
  | 'P2-mots'
  | 'P3-decouvertes'
  | 'P4-grandir'
  | 'P5-aventure-continue';

const MOCK_MISSION_STEP = 3; // étape courante (1..15)
const MOCK_MISSION_COMPLETED = false;
const MOCK_REACHED_MILESTONES: ReadonlyArray<5 | 10 | 15> = [];

/* === Helpers ========================================================== */

const HERO_BY_ID: ReadonlyMap<HeroId, HeroCell> = new Map(
  HERO_CELLS.filter((c) => c.id !== 'sage').map((c) => [c.id, c]),
);

function getHero(id: HeroId | null): HeroCell {
  if (!id) {
    return HERO_CELLS[0] as HeroCell;
  }
  const cell = HERO_BY_ID.get(id);
  if (!cell) {
    return HERO_CELLS[0] as HeroCell;
  }
  return cell;
}

function chapterStateLabel(state: ChapterState): string {
  switch (state) {
    case 'locked':      return 'Bientôt disponible';
    case 'available':   return 'Disponible';
    case 'in_progress': return 'En cours';
    case 'done':        return 'Terminé';
    case 'completed':   return 'Terminée';
  }
}

function missionStateLabel(state: MissionState): string {
  switch (state) {
    case 'not_started': return 'Pas encore commencée';
    case 'in_progress': return 'En cours';
    case 'completed':   return 'Terminée';
  }
}

/* === Component ======================================================== */

function DashboardMonde(): JSX.Element {
  const profile = getActiveProfile();
  const hero = useMemo(() => getHero(profile?.heroId ?? null), [profile?.heroId]);

  const missionState: MissionState = MOCK_MISSION_COMPLETED
    ? 'completed'
    : MOCK_MISSION_STEP > 0
      ? 'in_progress'
      : 'not_started';

  // Règle de débloqueio (D-MAP-02) :
  //  - Mission Rentrée : toujours disponible après choix héros
  //  - Diagnostic : toujours disponible (non-bloqueant)
  //  - P1 : requiert Mission Rentrée terminée
  //  - P2..P5 : chaîne séquentielle
  const chapters: ReadonlyArray<Chapter> = useMemo(() => {
    const missionDone = missionState === 'completed';
    return [
      {
        id: 'mission-rentree',
        title: 'Mission Rentrée',
        icon: '⭐',
        state: missionState === 'not_started'
          ? 'available'
          : missionState,
      },
      {
        id: 'diagnostic',
        title: 'Diagnostic',
        icon: '🔍',
        state: 'available', // parallèle, jamais bloqueant (D-MAP-02)
      },
      {
        id: 'P1-nombres',
        title: 'P1 — Les nombres',
        icon: '🔢',
        state: missionDone ? 'available' : 'locked',
        lockedNote: missionDone ? undefined : 'Après la Mission Rentrée',
      },
      {
        id: 'P2-mots',
        title: 'P2 — Les mots',
        icon: '📖',
        state: 'locked',
        lockedNote: 'Après P1',
      },
      {
        id: 'P3-decouvertes',
        title: 'P3 — Découvertes',
        icon: '🌱',
        state: 'locked',
        lockedNote: 'Après P2',
      },
      {
        id: 'P4-grandir',
        title: 'P4 — Grandir',
        icon: '🌟',
        state: 'locked',
        lockedNote: 'Après P3',
      },
      {
        id: 'P5-aventure-continue',
        title: 'P5 — L’aventure continue',
        icon: '🚀',
        state: 'locked',
        lockedNote: 'Après P4',
      },
    ];
  }, [missionState]);

  /* ----- Render: AppBar ----- */

  const appBar = (
    <div className={styles.appBar}>
      <div className={styles.appBarIdentity}>
        <HeroIllustration cell={hero} />
        <p className={styles.appBarHello}>
          Salut, <span data-testid="child-first-name">{profile?.firstName ?? "Aventurier"}</span>
          {' '}et{' '}
          <span className={styles.appBarHelloAccent} data-testid="hero-name">
            {hero.label}
          </span>
          {' !'}
        </p>
      </div>
      <Link
        to="/famille/accueil/"
        className={styles.sortieLink}
        data-action="sortie-parent"
      >
        Sortie parent
      </Link>
    </div>
  );

  /* ----- Render: Hero band ----- */

  const heroBand = (
    <section className={styles.heroBand} aria-label="Identité héro">
      <HeroIllustration cell={hero} pose="celebrate" />
      <div className={styles.heroBandLabel}>
        <h2 className={styles.heroBandTitle}>{hero.label}</h2>
        <p className={styles.heroBandRole}>{hero.role}</p>
      </div>
    </section>
  );

  /* ----- Render: Mission Card ----- */

  const missionCard = (() => {
    const badgeClass = missionState === 'completed'
      ? styles.missionBadgeCompleted
      : missionState === 'in_progress'
        ? styles.missionBadgeInProgress
        : styles.missionBadgeNotStarted;

    const intro = missionState === 'completed'
      ? 'Tu as renforcé tes pouvoirs. La porte de ta nouvelle année s\'ouvre !'
      : 'Réveille tes pouvoirs pour bien commencer l\'année !';

    const ctaLabel = missionState === 'completed'
      ? 'Découvrir mon parcours'
      : missionState === 'in_progress'
        ? `Continuer l'étape ${MOCK_MISSION_STEP}`
        : "Commencer l'étape 1";

    const ctaHref = missionState === 'completed'
      ? '/enfant/parcours-annuel/'
      : '/enfant/mission/rentree/';

    const progressPct = missionState === 'completed'
      ? 100
      : Math.round(((MOCK_MISSION_STEP - 1) / 15) * 100);

    return (
      <Card
        data-testid="mission-card"
        data-mission-state={missionState}
        magical
        childSized
        as="section"
        aria-labelledby="mission-card-title"
      >
        <div className={styles.missionCard}>
          <div className={styles.missionHeader}>
            <h2 id="mission-card-title" className={styles.missionTitle}>
              Mission Rentrée
            </h2>
            <span
              className={`${styles.missionBadge} ${badgeClass}`}
              data-testid="mission-badge"
            >
              {missionStateLabel(missionState)}
            </span>
          </div>

          <p className={styles.missionIntro}>{intro}</p>

          {missionState !== 'not_started' && (
            <div className={styles.missionProgress}>
              <p className={styles.missionProgressLabel}>
                Étape {MOCK_MISSION_STEP} sur 15
              </p>
              <div
                className={styles.missionProgressTrack}
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={progressPct}
                aria-label={`Progression Mission Rentrée : ${progressPct}%`}
              >
                <div
                  className={styles.missionProgressFill}
                  style={{ width: `${progressPct}%` }}
                  data-testid="mission-progress-fill"
                />
              </div>
              <div className={styles.missionMilestones} aria-label="Jalons 5/10/15">
                {[5, 10, 15].map((m) => {
                  const reached = MOCK_REACHED_MILESTONES.includes(m as 5 | 10 | 15);
                  return (
                    <span
                      key={m}
                      data-testid={`milestone-${m}`}
                      data-reached={reached || undefined}
                      className={reached ? styles.milestoneReached : undefined}
                    >
                      Jalon {m}/15
                    </span>
                  );
                })}
              </div>
            </div>
          )}

          <div>
            <Link to={ctaHref}>
              <Button
                tone="primary"
                size="lg"
                context="enfant"
                data-action="mission-cta"
                data-mission-state={missionState}
              >
                {ctaLabel}
              </Button>
            </Link>
          </div>
        </div>
      </Card>
    );
  })();

  /* ----- Render: Map Nodes ----- */

  const renderChapter = (chapter: Chapter) => {
    const interactive = chapter.state !== 'locked';
    const stateClass = chapter.state === 'locked'
      ? styles.mapNodeLocked
      : chapter.state === 'in_progress'
        ? styles.mapNodeInProgress
        : chapter.state === 'done'
          ? styles.mapNodeDone
          : styles.mapNodeAvailable;

    const baseProps = {
      key: chapter.id,
      'data-testid': `chapter-${chapter.id}`,
      'data-chapter-state': chapter.state,
      className: `${styles.mapNode} ${stateClass}`,
      'aria-label': interactive
        ? `${chapter.title} — ${chapterStateLabel(chapter.state)}`
        : `${chapter.title} — bientôt disponible`,
    };

    const inner = (
      <>
        <span className={styles.mapNodeIcon} aria-hidden="true">
          {chapter.icon}
        </span>
        <h3 className={styles.mapNodeTitle}>{chapter.title}</h3>
        <p className={styles.mapNodeState}>
          {chapterStateLabel(chapter.state)}
        </p>
        {chapter.lockedNote && (
          <p className={styles.mapNodeLockedNote}>{chapter.lockedNote}</p>
        )}
      </>
    );

    if (!interactive) {
      // Capítulo locked : bouton désactivé (focusable pour a11y mais sans action)
      return (
        <button
          {...baseProps}
          type="button"
          disabled
          aria-disabled="true"
        >
          {inner}
        </button>
      );
    }

    // Capítulo interactif : lien vers la route correspondante
    const href = chapter.id === 'mission-rentree'
      ? '/enfant/mission/rentree/'
      : chapter.id === 'diagnostic'
        ? '/enfant/diagnostic/'
        : `/enfant/parcours-annuel/${chapter.id}/`;

    return (
      <Link
        {...baseProps}
        to={href}
        data-action={`open-chapter-${chapter.id}`}
      >
        {inner}
      </Link>
    );
  };

  /* ----- Render: Footer ----- */

  const footer = (
    <nav className={styles.footerActions} aria-label="Actions rapides">
      <Link to="/enfant/mes-pouvoirs/">
        <Button
          tone="secondary"
          size="md"
          context="enfant"
          data-action="mes-pouvoirs"
        >
          Mes pouvoirs
        </Button>
      </Link>
      <Button
        tone="ghost"
        size="md"
        context="enfant"
        data-action="demander-sage"
        
      >
        Demander au Sage
      </Button>
    </nav>
  );

  /* ----- Render: Main content ----- */

  const visibleChapters = chapters.filter(
    (c) => c.id !== 'mission-rentree', // mission rendue séparément en destaque
  );

  return (
    <Layout
      context="enfant"
      width="child"
      header={<Header context="enfant" user={appBar} />}
      footer={footer}
    >
      <div className={styles.main}>
        <header>
          <p className={styles.eyebrow}>Mon monde</p>
          <h1 className={styles.title} data-testid="dashboard-title">
            Carte du monde magique
          </h1>
          <p className={styles.lede}>
            Voici ton chemin. Avance à ton rythme — chaque étape te donne de
            nouveaux pouvoirs.
          </p>
        </header>

        {heroBand}
        {missionCard}

        <section
          className={styles.mapSection}
          aria-label="Chapitres du parcours"
          data-testid="chapters-section"
        >
          <h2 className={styles.mapHeading}>Mes chapitres</h2>
          <div className={styles.mapGrid}>
            {visibleChapters.map(renderChapter)}
          </div>
        </section>
      </div>
    </Layout>
  );
}

export default DashboardMonde;
